import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ticketsQueryKeys } from 'features/tickets/queries';
import { notifyHostBalanceChanged } from 'features/session/hostBalanceBridge';

/**
 * Handles win-checking after events end and updating user balance.
 *
 * Deps are kept minimal to match the original EventViewer effects and
 * avoid infinite re-render loops (e.g. dispatching SET_USER creates a new
 * `user` object -- if `user` were a dep, the effect would re-fire).
 */
export function useWinChecker(params: {
  eventId: string | number | null | undefined;
  user: any;
  userDispatch: (action: { type: string; payload: any }) => void;
  run: { phase?: string; eventId?: string | number; [key: string]: unknown } | null;
  resultsHandler: {
    checkWins: (params: { event_id: string | number }) => void;
    checksLoading: boolean;
    winResults: { new_balance?: number } | null;
  };
  mediaHasEnded: boolean;
  mediaResults: Record<string, unknown>;
  setCurrentResult: (result: Record<string, unknown>) => void;
}) {
  const queryClient = useQueryClient();
  const {
    eventId,
    user,
    userDispatch,
    run,
    resultsHandler,
    mediaHasEnded,
    mediaResults,
    setCurrentResult,
  } = params;

  const userRef = React.useRef(user);
  userRef.current = user;
  const userDispatchRef = React.useRef(userDispatch);
  userDispatchRef.current = userDispatch;
  const checkWinsRef = React.useRef(resultsHandler.checkWins);
  checkWinsRef.current = resultsHandler.checkWins;
  const winResultsRef = React.useRef(resultsHandler.winResults);
  winResultsRef.current = resultsHandler.winResults;

  const lastCheckWinsEventIdRef = React.useRef<string | number | null>(null);
  const prevRunPhaseRef = React.useRef<string | undefined>(undefined);
  const prevEndedEventIdRef = React.useRef<string | number | null>(null);

  // Original dep: [HAS_ENDED]
  React.useEffect(() => {
    if (mediaHasEnded) {
      setCurrentResult(mediaResults);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaHasEnded]);

  // Original dep: [event_id, !!user]
  React.useEffect(() => {
    const currentUser = userRef.current;
    if (!currentUser || resultsHandler.checksLoading) return;
    if (eventId == null) return;
    if (lastCheckWinsEventIdRef.current !== null && String(lastCheckWinsEventIdRef.current) === String(eventId)) return;
    checkWinsRef.current({ event_id: eventId });
    lastCheckWinsEventIdRef.current = eventId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, !!user]);

  // Original dep: [resultsHandler.checksLoading]
  React.useEffect(() => {
    if (!resultsHandler.checksLoading && winResultsRef.current) {
      const currentUser = userRef.current;
      const new_user_data = {
        ...currentUser,
        player_balance: winResultsRef.current.new_balance,
      };
      userDispatchRef.current({ type: 'SET_USER', payload: new_user_data });
      notifyHostBalanceChanged(winResultsRef.current.new_balance);
      queryClient.invalidateQueries({ queryKey: ticketsQueryKeys.creditAvailable });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsHandler.checksLoading]);

  // Original dep: [run?.phase, run?.eventId, user]
  React.useEffect(() => {
    const prevPhase = prevRunPhaseRef.current;
    const nextPhase = run?.phase;
    const endedEventId = run?.phase === 'ended' ? run?.eventId : null;
    prevRunPhaseRef.current = nextPhase;
    const isTransitionToEnded = nextPhase === 'ended' && prevPhase !== 'ended';
    const isNewEndedRun = nextPhase === 'ended' && endedEventId != null && endedEventId !== prevEndedEventIdRef.current;
    if ((!isTransitionToEnded && !isNewEndedRun) || !endedEventId || !userRef.current) return;
    if (String(lastCheckWinsEventIdRef.current) === String(endedEventId)) return;
    prevEndedEventIdRef.current = endedEventId;
    lastCheckWinsEventIdRef.current = endedEventId;
    const checkWins = checkWinsRef.current;
    if (typeof checkWins === 'function') {
      checkWins({ event_id: endedEventId });
    }
  }, [run?.phase, run?.eventId]);

  return { lastCheckWinsEventIdRef };
}
